/**
 * DashboardPage.tsx — dashboard v2.1 (2026-08-05 visual redesign).
 *
 * Vahid on v2.0: "too complicated, two rows of numbers, no life, wasted
 * space." Same honest data, completely different presentation — a bento
 * grid with ONE focal point:
 *
 *   [ HERO (dark sage gradient): greeting + up to 3 BIG    ][ PULSE:      ]
 *   [ action tiles — the only large numbers on the page    ][ 2 slim flow ]
 *   [                                                      ][ bars + spark]
 *   [ NEEDS ATTENTION — one merged, aged list              ][ LATEST — dot]
 *   [ (watch warnings pinned on top)                       ][ timeline    ]
 *
 * Zero-count stages simply don't render. Four queue cards became one
 * list. Thirteen boxes became two slim bars. The sparkline is the only
 * chart, and it's real data (tests approved, 14 days).
 */
import { Box, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { BugReport } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../auth/useAuth';
import { usePermissions } from '../../hooks/usePermissions';
import { MoftakiBotIcon } from '../../components/shared/MoftakiBotIcon';
import { getDashboardData } from '../../services/dashboardService';
import { MobileDashboard } from './MobileDashboard';
import { MyTicketsAttention } from './MyTicketsAttention';
import { DashboardHero } from './DashboardHero';
import { PipelinePulse } from './PipelinePulse';
import { AttentionList } from './AttentionList';
import { ActivityCard } from './ActivityCard';
import type { DashboardData } from '../../types/dashboard';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isMobile) { setLoading(false); return; }
    getDashboardData().then(setData).catch(console.error).finally(() => setLoading(false));
  }, [isMobile]);

  if (isMobile) return <MobileDashboard />;

  const firstName = user?.displayName?.split(' ')[0] || 'there';

  return (
    <Box sx={{ pb: 2 }}>
      {/* Slim utility row above everything — no mega-tiles. */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mb: 1 }}>
        {!!user?.canAccessChat && (
          <Box onClick={() => navigate(ROUTES.MOFTAKIBOT)}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: '#7C4DFF' } }}>
            <MoftakiBotIcon size={18} />
            <Typography sx={{ fontSize: '0.76rem', fontWeight: 600 }}>Moftaki AI</Typography>
          </Box>
        )}
        <Box onClick={() => navigate(ROUTES.MY_FEEDBACK)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: '#E65100' } }}>
          <BugReport sx={{ fontSize: 16 }} />
          <Typography sx={{ fontSize: '0.76rem', fontWeight: 600 }}>My feedback</Typography>
        </Box>
      </Box>

      {/* Feedback tickets that need YOU — slim strip, unchanged. */}
      <MyTicketsAttention />

      {loading || !data ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)', gap: 1.75 }}>
          <Skeleton variant="rounded" height={230} sx={{ borderRadius: '18px' }} />
          <Skeleton variant="rounded" height={230} sx={{ borderRadius: '18px' }} />
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: '18px' }} />
          <Skeleton variant="rounded" height={360} sx={{ borderRadius: '18px' }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 1.75,
            alignItems: 'stretch',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 7fr) minmax(0, 5fr)' },
          }}
        >
          <DashboardHero firstName={firstName} data={data} />
          <PipelinePulse data={data} />
          <AttentionList data={data} />
          <ActivityCard activity={data.recentActivity} isAdmin={isAdmin} />
        </Box>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, ml: 0.5, opacity: 0.7 }}>
        Ages count days since last change · zero-count stages are hidden · every number is clickable
      </Typography>
    </Box>
  );
};
